import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

function InsightCompliance() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-btacc-blue z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="text-2xl font-montserrat font-bold text-white">
                BT<span className="text-btacc-gold">ACC</span>
              </h1>
              <p className="text-xs text-btacc-light font-inter -mt-1">Brazil Tax Advisory & Compliance Center</p>
            </Link>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link to="/" className="inline-flex items-center text-btacc-blue hover:text-btacc-gold transition mb-8">
            <ArrowLeft size={20} className="mr-2" />
            Back to Home
          </Link>

          {/* Article Header */}
          <div className="mb-8">
            <div className="text-sm text-btacc-secondary mb-4">BTACC Insights</div>
            <h1 className="text-4xl md:text-5xl font-montserrat font-bold text-btacc-blue mb-6">
              5 Critical Compliance Mistakes U.S. Exporters Make
            </h1>
            <p className="text-xl text-btacc-graphite font-inter">
              Avoid costly penalties and delays. Discover the most common compliance errors American companies make when shipping to Brazil.
            </p>
          </div>

          {/* Featured Image */}
          <img 
            src="/images/btacc-insight.png"
            alt="BTACC - Brazil Tax Advisory & Compliance Center"
            className="w-full h-96 object-cover rounded-lg mb-12"
          />

          {/* Article Body */}
          <div className="prose prose-lg max-w-none">
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              Brazilian customs and trade regulations are among the most complex in the world. Even experienced exporters can fall into common traps that result in shipment delays, fines, or rejected goods. Here are the five most critical mistakes we see—and how to avoid them.
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">1. Incorrect HS/NCM Classification</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Problem:</strong> Using the wrong tariff classification code is the #1 mistake. Brazil uses the NCM (Nomenclatura Comum do Mercosul) system, which is more granular than the HS code alone.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Solution:</strong> Work with specialists who understand both U.S. and Brazilian classification systems. A single digit error can mean the difference between a 10% and 35% tax rate.
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">2. Incomplete or Inaccurate Documentation</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Problem:</strong> Missing documents, incorrect values, or vague product descriptions trigger automatic holds at Brazilian customs.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>Required Documents:</strong>
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">Commercial Invoice (detailed product specifications)</li>
              <li className="mb-2">Packing List</li>
              <li className="mb-2">Bill of Lading or Air Waybill</li>
              <li className="mb-2">Certificate of Origin (when applicable)</li>
              <li className="mb-2">Import License (when required)</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">3. Undervaluing Shipments</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Problem:</strong> Some companies undervalue goods to reduce import taxes. Brazilian customs use sophisticated systems to detect this, and penalties are severe.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Consequences:</strong> Fines can be 100-400% of the undervalued amount, plus criminal charges in serious cases. Always declare true market value.
            </p>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">4. Ignoring Regulatory Requirements</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Problem:</strong> Many products require additional certifications from Brazilian agencies (ANVISA, INMETRO, ANATEL) before import. Companies often discover this too late.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>High-Risk Categories:</strong>
            </p>
            <ul className="list-disc pl-6 mb-6 text-btacc-graphite font-inter">
              <li className="mb-2">Medical devices and pharmaceuticals</li>
              <li className="mb-2">Cosmetics and personal care products</li>
              <li className="mb-2">Electronics and telecommunications equipment</li>
              <li className="mb-2">Food and beverages</li>
              <li className="mb-2">Automotive parts</li>
            </ul>

            <h2 className="text-2xl font-montserrat font-bold text-btacc-blue mt-8 mb-4">5. Poor Incoterms Selection</h2>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>The Problem:</strong> Choosing the wrong Incoterm creates confusion about who handles import duties, customs clearance, and associated costs.
            </p>
            <p className="text-btacc-graphite font-inter leading-relaxed mb-6">
              <strong>Recommendation for Brazil:</strong> DDP (Delivered Duty Paid) or DAP (Delivered At Place) terms provide clarity, but require the exporter to understand Brazilian import processes thoroughly.
            </p>

            <div className="bg-btacc-light p-6 rounded-lg mt-8 mb-8">
              <h3 className="text-lg font-montserrat font-semibold text-btacc-blue mb-3">Pre-Shipment Review Service</h3>
              <p className="text-btacc-graphite font-inter mb-4">
                Don't let compliance mistakes derail your Brazilian operations. BTACC offers pre-shipment reviews to catch errors before they become problems.
              </p>
              <button onClick={handleContactClick} className="inline-block bg-btacc-blue text-white px-6 py-3 rounded-md font-montserrat font-semibold hover:bg-btacc-secondary transition cursor-pointer">
                Request a Review
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="bg-btacc-blue text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/" className="inline-block mb-4">
            <h2 className="text-2xl font-montserrat font-bold">
              BT<span className="text-btacc-gold">ACC</span>
            </h2>
          </Link>
          <div className="text-sm text-btacc-light mb-4">
            <p>Brazil Tax Advisory & Compliance Center</p>
            <p>320 Decker Drive, Irving, Texas 75062</p>
          </div>
          <p className="text-sm text-btacc-light">
            © {new Date().getFullYear()} BTACC — All rights reserved. / Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default InsightCompliance;